import React, { useEffect, useRef } from 'react';

/**
 * AmbientAurora — full-viewport cursor/touch/gyro-reactive gradient orbs.
 * - Desktop: 3 huge blurred orbs (cyan/gold/violet) that smoothly chase the pointer with different lag → gooey aurora feel.
 * - Mobile: subscribes to touchmove + DeviceOrientationEvent (gamma/beta) for tilt-reactive parallax.
 * - Fixed, pointer-events-none, sits behind ALL content but above page background.
 * - Adds a subtle noise/grain layer for depth (removes AI-slop flat gradient look).
 */
const AmbientAurora = () => {
  const rootRef = useRef(null);
  const o1Ref = useRef(null);
  const o2Ref = useRef(null);
  const o3Ref = useRef(null);
  // Target (raw input) + eased (rendered) positions
  const target = useRef({ x: 0.5, y: 0.4 });
  const eased = useRef([
    { x: 0.5, y: 0.4 },
    { x: 0.5, y: 0.4 },
    { x: 0.5, y: 0.4 },
  ]);
  const rafRef = useRef(null);

  useEffect(() => {
    const isTouch = typeof window !== 'undefined' && 'ontouchstart' in window;

    const onMove = (e) => {
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      target.current.x = e.clientX / w;
      target.current.y = e.clientY / h;
    };

    const onTouch = (e) => {
      const t = e.touches && e.touches[0];
      if (!t) return;
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      target.current.x = t.clientX / w;
      target.current.y = t.clientY / h;
    };

    const onTilt = (e) => {
      // gamma: -90..90 (left/right), beta: -180..180 (front/back)
      const g = Math.max(-45, Math.min(45, e.gamma || 0));
      const b = Math.max(-45, Math.min(45, (e.beta || 0) - 30));
      // Map to 0..1
      target.current.x = 0.5 + g / 90;
      target.current.y = 0.5 + b / 90;
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('touchmove', onTouch, { passive: true });
    if (isTouch && typeof window.DeviceOrientationEvent !== 'undefined') {
      window.addEventListener('deviceorientation', onTilt, { passive: true });
    }

    // Different follow speeds → gooey lag effect
    const speeds = [0.08, 0.045, 0.025];

    const loop = () => {
      const t = target.current;
      for (let i = 0; i < 3; i++) {
        const s = speeds[i];
        eased.current[i].x += (t.x - eased.current[i].x) * s;
        eased.current[i].y += (t.y - eased.current[i].y) * s;
      }
      // Apply transforms — spread orbs around the pointer so they don't stack
      const [e1, e2, e3] = eased.current;
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      if (o1Ref.current) {
        o1Ref.current.style.transform = `translate3d(${(e1.x * w) - 300}px, ${(e1.y * h) - 300}px, 0)`;
      }
      if (o2Ref.current) {
        o2Ref.current.style.transform = `translate3d(${(e2.x * w) - 260 + 120}px, ${(e2.y * h) - 260 - 60}px, 0)`;
      }
      if (o3Ref.current) {
        o3Ref.current.style.transform = `translate3d(${(e3.x * w) - 260 - 120}px, ${(e3.y * h) - 260 + 80}px, 0)`;
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onTouch);
      window.removeEventListener('deviceorientation', onTilt);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
      data-testid="ambient-aurora"
    >
      {/* Gradient orbs — GPU-transformed */}
      <div
        ref={o1Ref}
        className="absolute w-[600px] h-[600px] rounded-full will-change-transform"
        style={{
          background: 'radial-gradient(circle, rgba(0,212,255,0.28) 0%, rgba(0,212,255,0.10) 40%, transparent 70%)',
          filter: 'blur(80px)',
          mixBlendMode: 'screen',
        }}
      />
      <div
        ref={o2Ref}
        className="absolute w-[520px] h-[520px] rounded-full will-change-transform"
        style={{
          background: 'radial-gradient(circle, rgba(212,175,55,0.24) 0%, rgba(212,175,55,0.08) 40%, transparent 70%)',
          filter: 'blur(90px)',
          mixBlendMode: 'screen',
        }}
      />
      <div
        ref={o3Ref}
        className="absolute w-[520px] h-[520px] rounded-full will-change-transform"
        style={{
          background: 'radial-gradient(circle, rgba(139,92,246,0.20) 0%, rgba(139,92,246,0.06) 40%, transparent 70%)',
          filter: 'blur(100px)',
          mixBlendMode: 'screen',
        }}
      />

      {/* Static subtle vignette for depth (never blocks content) */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_10%,rgba(0,0,0,0.35)_60%,rgba(0,0,0,0.75)_100%)]" />

      {/* Fine grain overlay — kills the flat AI-gradient look */}
      <div
        className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.9'/></svg>\")",
        }}
      />
    </div>
  );
};

export default AmbientAurora;
